/**
 * @fileoverview SVG line chart for time-series data with optional stacking
 * @module ctl-line-chart
 * @version 1.0.0
 *
 * @description
 * Renders a responsive SVG line chart. Supports multiple data series,
 * optional stacking, a legend, grid lines, and axis labels.
 * Listens for `ctl-date-range-change` events on its root to re-render
 * filtered data when a date range picker is present on the page.
 *
 * @example
 * <ctl-line-chart
 *   title="Website Visits"
 *   x-labels='["Jan","Feb","Mar","Apr","May","Jun"]'
 *   series='[{"label":"Visits","color":"#6200ee","values":[120,180,150,220,190,260]},
 *             {"label":"Leads","color":"#03dac6","values":[20,35,28,42,38,55]}]'>
 * </ctl-line-chart>
 */

import { safeJsonParse } from '../../../utils/safeJsonParse.js';
import "../foundation/ctl-skeleton.js";

const PAD = { top: 16, right: 20, bottom: 48, left: 52 };

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host {
    display: block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    color: var(--ctl-color-text);
  }

  .card {
    background: var(--ctl-color-surface);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    box-shadow: var(--ctl-shadow-card);
    overflow: hidden;
  }

  .head {
    padding: var(--ctl-space-3-5) var(--ctl-space-4);
    border-bottom: 1px solid var(--ctl-color-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ctl-space-3);
    flex-wrap: wrap;
  }

  .title {
    font-size: var(--ctl-font-size-lg);
    font-weight: var(--ctl-font-weight-heavy);
    line-height: 1.2;
  }

  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: var(--ctl-space-3);
    align-items: center;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-1-5);
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-muted);
    cursor: pointer;
    user-select: none;
  }

  .legend-swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .chart-wrap {
    padding: var(--ctl-space-3) var(--ctl-space-2) var(--ctl-space-2);
  }

  svg {
    display: block;
    width: 100%;
    overflow: visible;
  }

  .grid-line { stroke: var(--ctl-color-border); stroke-dasharray: 4 4; }
  .axis-line { stroke: var(--ctl-color-border); }
  .axis-label { fill: var(--ctl-color-muted); font-size: 11px; }
  .axis-title { fill: var(--ctl-color-muted); font-size: 11px; }
  .data-line { fill: none; stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round; }
  .data-area { opacity: 0.12; }
  .dot { cursor: pointer; transition: r 100ms; }

  .tooltip {
    position: absolute;
    background: var(--ctl-color-surface-inverse);
    color: var(--ctl-color-on-surface-inverse);
    border-radius: var(--ctl-radius-md);
    padding: var(--ctl-space-2) var(--ctl-space-3);
    font-size: var(--ctl-font-size-sm);
    pointer-events: none;
    white-space: nowrap;
    box-shadow: var(--ctl-shadow-dropdown);
    opacity: 0;
    transition: opacity 100ms;
    z-index: 10;
  }

  .tooltip.visible { opacity: 1; }

  .wrap {
    position: relative;
  }

  :host([loading]) .real { display: none; }
  .loading { display: none; padding: var(--ctl-space-4); }
  :host([loading]) .loading { display: block; }
</style>

<div class="card" part="card">
  <div class="head" part="head">
    <div class="title" id="title"></div>
    <div class="legend" id="legend" part="legend"></div>
  </div>
  <div class="real">
    <div class="wrap" part="wrap">
      <div class="chart-wrap" part="chart-wrap">
        <svg id="svg" role="img" aria-label="Line chart"></svg>
      </div>
      <div class="tooltip" id="tooltip"></div>
    </div>
  </div>
  <div class="loading" part="loading" aria-hidden="true">
    <ctl-skeleton variant="rect" height="220px"></ctl-skeleton>
  </div>
</div>
`;

// Default palette
const DEFAULT_COLORS = [
  "#6200ee", "#03dac6", "#f59e0b", "#10b981", "#ef4444",
  "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"
];

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

export class CtlLineChart extends HTMLElement {
  static get observedAttributes() {
    return ["title", "x-labels", "series", "stacked", "loading", "x-label", "y-label", "height"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._titleEl = this.shadowRoot.getElementById("title");
    this._legendEl = this.shadowRoot.getElementById("legend");
    this._svg = this.shadowRoot.getElementById("svg");
    this._tooltip = this.shadowRoot.getElementById("tooltip");
    this._series = [];
    this._xLabels = [];
    this._hidden = new Set();
    this._onRangeChange = this._onRangeChange.bind(this);
    this._onResize = this._onResize.bind(this);
    this._ro = new ResizeObserver(this._onResize);
  }

  connectedCallback() {
    this._parse();
    this._bindEvents();
    this._render();
    this._ro.observe(this);
    this.getRootNode().addEventListener("ctl-date-range-change", this._onRangeChange);
  }

  disconnectedCallback() {
    this._ro.unobserve(this);
    this.getRootNode().removeEventListener("ctl-date-range-change", this._onRangeChange);
  }

  /** Bind all interactive events once using delegation. */
  _bindEvents() {
    // Legend: toggle series visibility
    this._legendEl.addEventListener("click", (e) => {
      const item = e.target.closest(".legend-item");
      if (!item) return;
      const idx = parseInt(item.dataset.seriesIdx, 10);
      if (Number.isNaN(idx)) return;
      if (this._hidden.has(idx)) this._hidden.delete(idx);
      else this._hidden.add(idx);
      this._render();
    });
    this._legendEl.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const item = e.target.closest(".legend-item");
      if (!item) return;
      e.preventDefault();
      const idx = parseInt(item.dataset.seriesIdx, 10);
      if (Number.isNaN(idx)) return;
      if (this._hidden.has(idx)) this._hidden.delete(idx);
      else this._hidden.add(idx);
      this._render();
    });

    // Dots: tooltip via mouseover / mouseout / focusin / focusout
    this._svg.addEventListener("mouseover", (e) => {
      const dot = e.target.closest(".dot");
      if (!dot) return;
      const from = e.relatedTarget instanceof Element ? e.relatedTarget.closest(".dot") : null;
      if (from === dot) return;
      dot.setAttribute("r", "6");
      this._showTooltip(e, dot.dataset.name, dot.dataset.label, dot.dataset.value);
    });
    this._svg.addEventListener("mouseout", (e) => {
      const dot = e.target.closest(".dot");
      if (!dot) return;
      const to = e.relatedTarget instanceof Element ? e.relatedTarget.closest(".dot") : null;
      if (to === dot) return;
      dot.setAttribute("r", "4");
      this._hideTooltip();
    });
    this._svg.addEventListener("focusin", (e) => {
      const dot = e.target.closest(".dot");
      if (!dot) return;
      this._showTooltip(e, dot.dataset.name, dot.dataset.label, dot.dataset.value);
    });
    this._svg.addEventListener("focusout", (e) => {
      const dot = e.target.closest(".dot");
      if (!dot) return;
      this._hideTooltip();
    });
  }

  attributeChangedCallback(name) {
    if (name === "x-labels" || name === "series") this._parse();
    this._render();
  }

  // --- Public API ---
  get title() { return this.getAttribute("title") || ""; }
  set title(v) { this.setAttribute("title", String(v ?? "")); }

  get stacked() { return this.hasAttribute("stacked"); }
  set stacked(v) { v ? this.setAttribute("stacked", "") : this.removeAttribute("stacked"); }

  get loading() { return this.hasAttribute("loading"); }
  set loading(v) { v ? this.setAttribute("loading", "") : this.removeAttribute("loading"); }

  get series() { return this._series; }
  set series(v) {
    this._series = Array.isArray(v) ? v : [];
    this.setAttribute("series", JSON.stringify(this._series));
  }

  get xLabels() { return this._xLabels; }
  set xLabels(v) {
    this._xLabels = Array.isArray(v) ? v : [];
    this.setAttribute("x-labels", JSON.stringify(this._xLabels));
  }

  // --- Private ---
  _parse() {
    const raw = this.getAttribute("series");
    if (raw) this._series = safeJsonParse(raw, []);
    const lraw = this.getAttribute("x-labels");
    if (lraw) this._xLabels = safeJsonParse(lraw, []);
  }

  _onRangeChange(e) {
    const { start, end } = e.detail || {};
    if (!start || !end) return;
    // Re-render when the date range changes; consumers can also filter
    // the series data externally and update the `series` property.
    this._render();
  }

  _onResize() { this._render(); }

  _visibleSeries() {
    return this._series.map((s, i) => ({
      ...s,
      color: s.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
      visible: !this._hidden.has(i)
    }));
  }

  _render() {
    this._titleEl.textContent = this.title;
    if (this.loading) return;

    const series = this._visibleSeries();
    this._renderLegend(series);

    const wrap = this.shadowRoot.querySelector(".chart-wrap");
    const W = wrap.clientWidth || 600;
    const H = parseInt(this.getAttribute("height") || "240", 10);

    const svg = this._svg;
    svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
    svg.setAttribute("height", H);
    svg.setAttribute("aria-label", `Line chart: ${this.title}`);

    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;

    const xLabels = this._xLabels;
    const pointCount = xLabels.length || Math.max(...series.map(s => (s.values || []).length), 0);
    if (pointCount < 2) { svg.innerHTML = ""; return; }

    // Stack values if stacked
    const stacked = this.stacked;
    let stackedMax = 0;
    let rawMax = 0;
    series.forEach(s => {
      (s.values || []).forEach(v => { rawMax = Math.max(rawMax, v || 0); });
    });

    let stackBase = null;
    if (stacked) {
      stackBase = new Array(pointCount).fill(0);
      series.forEach(s => {
        if (!s.visible) return;
        (s.values || []).forEach((v, i) => {
          stackBase[i] = (stackBase[i] || 0) + (v || 0);
        });
      });
      stackedMax = Math.max(...stackBase);
    }

    const yMax = stacked ? stackedMax : rawMax;
    const yNice = this._niceMax(yMax);

    const xStep = innerW / Math.max(pointCount - 1, 1);
    const yScale = (v) => PAD.top + innerH - (v / yNice) * innerH;
    const xScale = (i) => PAD.left + i * xStep;

    let html = "";

    // Grid lines
    const yTicks = 5;
    for (let t = 0; t <= yTicks; t++) {
      const yv = (yNice / yTicks) * t;
      const y = yScale(yv);
      html += `<line class="grid-line" x1="${PAD.left}" y1="${y}" x2="${PAD.left + innerW}" y2="${y}"/>`;
      html += `<text class="axis-label" x="${PAD.left - 6}" y="${y + 4}" text-anchor="end">${this._formatY(yv)}</text>`;
    }

    // X axis labels
    for (let i = 0; i < pointCount; i++) {
      const x = xScale(i);
      const label = xLabels[i] != null ? String(xLabels[i]) : String(i + 1);
      // Show every Nth label to avoid crowding
      const step = Math.ceil(pointCount / 10);
      if (i % step === 0 || i === pointCount - 1) {
        html += `<text class="axis-label" x="${x}" y="${PAD.top + innerH + 20}" text-anchor="middle">${this._esc(label)}</text>`;
      }
    }

    // Axis titles
    const xTitle = this.getAttribute("x-label");
    const yTitle = this.getAttribute("y-label");
    if (xTitle) {
      html += `<text class="axis-title" x="${PAD.left + innerW / 2}" y="${H - 4}" text-anchor="middle">${this._esc(xTitle)}</text>`;
    }
    if (yTitle) {
      html += `<text class="axis-title" x="12" y="${PAD.top + innerH / 2}" text-anchor="middle" transform="rotate(-90, 12, ${PAD.top + innerH / 2})">${this._esc(yTitle)}</text>`;
    }

    // Axis lines
    html += `<line class="axis-line" x1="${PAD.left}" y1="${PAD.top}" x2="${PAD.left}" y2="${PAD.top + innerH}"/>`;
    html += `<line class="axis-line" x1="${PAD.left}" y1="${PAD.top + innerH}" x2="${PAD.left + innerW}" y2="${PAD.top + innerH}"/>`;

    // Stacking accumulator
    const stackAcc = new Array(pointCount).fill(0);

    // Draw areas then lines (areas first for proper layering)
    const visibleSeries = series.filter(s => s.visible);

    // Areas
    visibleSeries.forEach(s => {
      const vals = s.values || [];
      if (vals.length < 2) return;
      const pts = vals.map((v, i) => {
        const base = stacked ? stackAcc[i] : 0;
        return { x: xScale(i), y: yScale(base + (v || 0)), base: yScale(base) };
      });
      if (stacked) vals.forEach((v, i) => { stackAcc[i] += v || 0; });

      const d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
      const dClose = `${d} L${pts[pts.length - 1].x.toFixed(1)},${pts[pts.length - 1].base.toFixed(1)} ${pts.slice().reverse().map((p, i) => `${i === 0 ? "L" : "L"}${p.x.toFixed(1)},${p.base.toFixed(1)}`).join(" ")} Z`;
      html += `<path class="data-area" fill="${s.color}" d="${dClose}"/>`;
    });

    // Reset for lines
    stackAcc.fill(0);

    // Lines + dots
    visibleSeries.forEach((s, si) => {
      const vals = s.values || [];
      if (vals.length < 2) return;
      const pts = vals.map((v, i) => {
        const base = stacked ? stackAcc[i] : 0;
        return { x: xScale(i), y: yScale(base + (v || 0)), v: base + (v || 0) };
      });
      if (stacked) vals.forEach((v, i) => { stackAcc[i] += v || 0; });

      // Smooth line using cardinal spline
      const d = this._smoothPath(pts);
      html += `<path class="data-line" stroke="${s.color}" d="${d}" data-series="${si}"/>`;

      // Dots
      pts.forEach((p, i) => {
        const label = xLabels[i] != null ? String(xLabels[i]) : String(i + 1);
        html += `<circle class="dot"
          cx="${p.x.toFixed(1)}"
          cy="${p.y.toFixed(1)}"
          r="4"
          fill="${s.color}"
          stroke="var(--ctl-color-surface)"
          stroke-width="2"
          data-series="${si}"
          data-i="${i}"
          data-label="${this._esc(label)}"
          data-value="${p.v}"
          data-name="${this._esc(s.label || "")}"
          tabindex="0"
          role="button"
          aria-label="${this._esc(s.label || "")}: ${p.v} at ${this._esc(label)}"
        />`;
      });
    });

    svg.innerHTML = html;
  }

  _smoothPath(pts) {
    if (pts.length < 2) return "";
    const d = [`M${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`];
    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1];
      const curr = pts[i];
      const cpx = (prev.x + curr.x) / 2;
      d.push(`C${cpx.toFixed(1)},${prev.y.toFixed(1)} ${cpx.toFixed(1)},${curr.y.toFixed(1)} ${curr.x.toFixed(1)},${curr.y.toFixed(1)}`);
    }
    return d.join(" ");
  }

  /** Update legend HTML only; event handling is delegated via _bindEvents(). */
  _renderLegend(series) {
    this._legendEl.innerHTML = series.map((s, i) => `
      <div class="legend-item" data-series-idx="${i}" role="checkbox" aria-checked="${s.visible}" tabindex="0">
        <div class="legend-swatch" style="background:${s.color};opacity:${s.visible ? 1 : 0.3};"></div>
        <span style="opacity:${s.visible ? 1 : 0.4}">${this._esc(s.label || `Series ${i + 1}`)}</span>
      </div>
    `).join("");
  }

  _showTooltip(e, name, label, value) {
    const tip = this._tooltip;
    tip.innerHTML = `<strong>${this._esc(name)}</strong><br>${this._esc(label)}: ${this._esc(this._formatY(parseFloat(value)))}`;
    tip.classList.add("visible");
    const wrap = this.shadowRoot.querySelector(".wrap");
    const rect = wrap.getBoundingClientRect();
    const ex = e.clientX - rect.left + 12;
    const ey = e.clientY - rect.top - 36;
    tip.style.left = `${clamp(ex, 0, rect.width - 140)}px`;
    tip.style.top = `${clamp(ey, 0, rect.height - 60)}px`;
  }

  _hideTooltip() {
    this._tooltip.classList.remove("visible");
  }

  _niceMax(v) {
    if (!v || v <= 0) return 10;
    const exp = Math.pow(10, Math.floor(Math.log10(v)));
    const frac = v / exp;
    const nice = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
    return nice * exp;
  }

  _formatY(v) {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}k`;
    return String(Math.round(v));
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }
}

if (!customElements.get("ctl-line-chart")) {
  customElements.define("ctl-line-chart", CtlLineChart);
}
